import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApiEntity } from 'app/api/models/api-entity.model';
import { ApiDependenciesService } from 'app/api/services/api-dependencies.service';
import { ContactFormModel } from 'app/modules/contacts/models/contact-form.model';
import { ContactListItemViewModel } from 'app/modules/contacts/models/contact-list-item-view.model';
import { ContactListItemModel } from 'app/modules/contacts/models/contact-list-item.model';
import { LookupList } from 'app/shared/models/lookup-list.model';
import { ListServiceBase } from 'app/shared/services/list-base.service';
import { Division } from 'app/shared/enumeration/division';
import { UserCompanyLookupDataModel } from 'app/modules/contacts/models/user-company-lookup-data.model'


@Injectable()
export class UserContactListService extends ListServiceBase {
  divisionId = Division.Supportis;

  constructor(protected dependencies: ApiDependenciesService, protected dialog: MatDialog) {
    super(dependencies, dialog);

    this.initialiseFiltersSortAndPaging();
  }

  // populate initial filter properties
  private initialiseFiltersSortAndPaging() {
    // Add initialisation here
  }

  //#region Get Endpoints

  get baseEndpoint(): string {
    return `/divisions/${this.divisionId}/contacts`;
  }
  baseEndpointWithId(id: number): string {
    return this.baseEndpoint + '/' + id;
  }

  // a number of endpoint getters are defined in ListServiceBase
  // not all endpoints will be required for all implementations

  // OVERRIDDEN from ListServiceBase
  protected getCommandStubEndpoint(itemId: number): string {
    return this.baseEndpointWithId(itemId);
  }

  // OVERRIDDEN from ListServiceBase
  protected getListEndpoint(): string {
    return this.baseEndpoint + '/user-contact-list';
  }

  //#region list loading

  // OVERRIDDEN from ListServiceBase
  // Create a new ViewModel specific to this list
  // Not necessary to set the view model itemId as it is set in the viewModel constructor
  createListViewModel(dataModel: ApiEntity) {
    return new ContactListItemViewModel(dataModel as ContactListItemModel);
  }

  // OVERRIDDEN from ListServiceBase
  // Map to data model from the API response item for each list row item
  // There is an assumption here that the response item directly maps to the angular model and so can just be assigned
  getMappedListItemDataModel(responseItem: any): ApiEntity {
    const model: ContactListItemModel = responseItem;
    return model;
  }

  //#region get details

  // OVERRIDDEN from ListServiceBase
  // Map to data model from the API response item for the detail item (create/update)
  // The model must be derived from ApiEntity
  // There is an assumption here that the response item directly maps to the angular model and so can just be assigned
  getMappedEditableItemDataModel(response: any): ApiEntity {
    const model: ContactFormModel = response;
    return model;
  }

  // #endregion

  protected getCreateEndpoint(): string {
    return this.baseEndpoint;
  }

  // Call base method to proces the command
  // Make sure that the getCommandStubEndpoint has been overridden
  async activateToggle(itemId: number) {
    await this.sendCommand(itemId, 'activate-toggle');
  }

  // OVERRIDDEN from ListServiceBase
  protected getGetEditableItemEndpoint(itemId: number) {
    return itemId ? this.baseEndpointWithId(itemId) : this.baseEndpoint + '/blank';
  }

  async getUserCompanyLookupData(): Promise<UserCompanyLookupDataModel> {
    // const divisionId = this.dependencies.cookieService.getTheme();
    const endpoint = `/divisions/${this.divisionId}/contacts/user-company-lookup-data`;
    this.blockUI.start();
    let model: UserCompanyLookupDataModel;
    await this.setEndpoint(endpoint)
      .getSingle()
      .then((response) => {
        model = response;
        this.blockUI.stop();
      })
      .catch(() => {
        this.blockUI.stop();
      });

    return Promise.resolve(model);
  }

  async getFilterLookupData(): Promise<ApiEntity> {
    let model: ApiEntity;
    // const divisionId = this.dependencies.cookieService.getTheme();
    let endpoint = `/divisions/${this.divisionId}/contacts/get-search-filter`;

    await this.setEndpoint(endpoint)
      .getSingle()
      .then((response) => {
        model = response;
      });
    return Promise.resolve(model);
  }

  async isEmailUniqueInContacts(id: number, value: any): Promise<boolean> {
    let result = true;

    const endpoint = this.baseEndpointWithId(id) + '/is-contact-email-unique?email=' + value;

    await this.setEndpoint(endpoint)
      .getSingle()
      .then((response: boolean) => {
        result = response;
      });

    return Promise.resolve(result);
  }

  async isEmailUniqueInUsers(id: number, value: any): Promise<boolean> {
    let result = true;

    const endpoint = this.baseEndpointWithId(id) + '/is-user-email-unique?email=' + value;

    await this.setEndpoint(endpoint)
      .getSingle()
      .then((response: boolean) => {
        result = response;
      });

    return Promise.resolve(result);
  }
}
